import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { Button, Card, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import api from "../../config/api";
import { getDate, LocalFormatDate, rupeeFormat } from "../../config/functions";
import {
    ClientRegistrationType,
    fetchClientRegistrationAction,
} from "../../redux/actions/clientRegistration";
import { AppDispatch, StoreState } from "../../redux/store";
import {
    buildInvoiceWiseDisplayRows,
    computeInvoiceWiseGrandTotals,
    type InvoiceData,
    type InvoiceWiseDisplayRow,
} from "../../utils/salesInvoiceWiseReport";
import SalesReturnReportFilter from "./SalesReturnReportFilter";
import ReportGrandTotalBar from "./ReportGrandTotalBar";

const buildPdfTableBody = (rows: InvoiceWiseDisplayRow[]) => {
    const body: (string | Record<string, unknown>)[][] = [];

    rows.forEach((item) => {
        if (item.isFirstLineOfInvoice) {
            const span = item.invoiceRowSpan;
            body.push([
                { content: String(item.siNo), rowSpan: span },
                { content: LocalFormatDate(item.date) || "", rowSpan: span },
                { content: item.day || "", rowSpan: span },
                { content: item.invoiceNo || "", rowSpan: span },
                { content: item.customerName || "", rowSpan: span },
                {
                    content: rupeeFormat(item.billValue) || "0",
                    rowSpan: span,
                },
                String(item.quantity ?? 0),
                rupeeFormat(item.taxable) || "0",
                rupeeFormat(item.igst) || "0",
                rupeeFormat(item.cgst) || "0",
                rupeeFormat(item.sgst) || "0",
                rupeeFormat(item.total) || "0",
                {
                    content: rupeeFormat(item.paid) || "0",
                    rowSpan: span,
                },
                {
                    content: rupeeFormat(item.balance) || "0",
                    rowSpan: span,
                },
                { content: item.splitMode || "", rowSpan: span },
            ]);
        } else {
            body.push([
                String(item.quantity ?? 0),
                rupeeFormat(item.taxable) || "0",
                rupeeFormat(item.igst) || "0",
                rupeeFormat(item.cgst) || "0",
                rupeeFormat(item.sgst) || "0",
                rupeeFormat(item.total) || "0",
            ]);
        }
    });

    return body;
};

const SalesReturnInvoiceWise = () => {
    const dispatch = useDispatch<AppDispatch>();

    const [fromDate, setFromDate] = useState<string>("");
    const [toDate, setToDate] = useState<string>("");
    const [branchId, setBranchId] = useState<string>("");
    const [invoiceTitle, setInvoiceTitle] = useState<string>("All");
    const [invoiceType, setInvoiceType] = useState<string>("Retail");
    const [tableData, setTableData] = useState<InvoiceData[]>([]);
    const [triggerSearch, setTriggerSearch] = useState<boolean>(false);
    const [hasSearched, setHasSearched] = useState(false);

    const clients = useSelector<StoreState, ClientRegistrationType[]>(
        (state) => state.clientRegistration,
    );

    const displayRows = useMemo(
        () => buildInvoiceWiseDisplayRows(tableData),
        [tableData],
    );

    const grandTotal = useMemo(
        () => computeInvoiceWiseGrandTotals(tableData),
        [tableData],
    );

    useEffect(() => {
        dispatch(fetchClientRegistrationAction());
    }, [dispatch]);

    useEffect(() => {
        if (triggerSearch) {
            api()
                .get(
                    `reports/salesReturn/?fromDate=${getDate(fromDate)}&toDate=${getDate(
                        toDate,
                    )}&branchId=${branchId}&invoiceTitle=${invoiceTitle}&invoiceType=${invoiceType}`,
                )
                .then((res) => {
                    if (res.status === 200 && res.data.data) {
                        setTableData(res.data.data);
                    }
                })
                .catch((error) => {
                    console.error("Error fetching sales return data:", error);
                });

            setTriggerSearch(false);
        }
    }, [triggerSearch, fromDate, toDate, branchId, invoiceTitle, invoiceType]);

    const [filters, setFilters] = useState<Record<string, string>>({});

    const handleSearch = (newFilters: Record<string, string>) => {
        setFilters(newFilters);
        setFromDate(newFilters.fromDate);
        setToDate(newFilters.toDate);
        setBranchId(newFilters.branchId);
        setInvoiceTitle(newFilters.invoiceTitle);
        setInvoiceType(newFilters.invoiceType);
        setHasSearched(true);
        setTriggerSearch(true);
    };

    const downloadCSV = () => {
        const header = [
            "SI.No",
            "Financial Year",
            "Date",
            "Day",
            "Invoice Number",
            "Name",
            "Bill Value",
            "Qty",
            "Taxable",
            "IGST",
            "CGST",
            "SGST",
            "Total",
            "Paid",
            "Balance",
            "Split Mode",
        ].map((col) => `="${col}"`);

        const rows = displayRows.map((item) => {
            if (item.isFirstLineOfInvoice) {
                return [
                    item.siNo,
                    item.financialYear,
                    LocalFormatDate(item.date),
                    item.day,
                    item.invoiceNo,
                    item.customerName,
                    item.billValue,
                    item.quantity,
                    item.taxable,
                    item.igst,
                    item.cgst,
                    item.sgst,
                    item.total,
                    item.paid,
                    item.balance,
                    item.splitMode,
                ];
            }
            return [
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                item.quantity,
                item.taxable,
                item.igst,
                item.cgst,
                item.sgst,
                item.total,
                "",
                "",
                "",
            ];
        });

        rows.push([
            "",
            "",
            "",
            "",
            "",
            "Grand Total",
            grandTotal.billValue.toFixed(2),
            "",
            grandTotal.taxable.toFixed(2),
            grandTotal.igst.toFixed(2),
            grandTotal.cgst.toFixed(2),
            grandTotal.sgst.toFixed(2),
            grandTotal.lineTotal.toFixed(2),
            grandTotal.paid.toFixed(2),
            grandTotal.balance.toFixed(2),
            "",
        ]);

        const csvContent = [
            header.join(","),
            ...rows.map((row) => row.join(",")),
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "sales_return_invoice_details.csv";
        link.click();
    };

    const downloadPDF = () => {
        const logoUrl = clients[0]?.shopPhoto;

        const doc = new jsPDF("landscape");
        const marginX = 10;
        const boxWidth = doc.internal.pageSize.width - 2 * marginX;
        const boxHeight = 25;
        const startY = 15;

        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");

        doc.rect(marginX, startY - 5, boxWidth, boxHeight);

        if (logoUrl) {
            doc.addImage(logoUrl, "JPEG", marginX + 5, startY - 2, 25, 20);
        }

        const paddingBetweenHeaderAndTable = 3;
        const textX = marginX + 35;

        doc.text(clients[0]?.shopFullName || "", textX, startY);
        doc.setFont("helvetica", "normal");
        doc.text(
            `${clients[0]?.address1 || ""}, ${clients[0]?.address2 || ""}, ${clients[0]?.district || ""}`,
            textX,
            startY + 5,
        );
        doc.text(
            `${clients[0]?.state || ""} - ${clients[0]?.pincode || ""}, ${clients[0]?.contactNo1 || ""}`,
            textX,
            startY + 10,
        );
        doc.text(`GSTIN: ${clients[0]?.gstNo || ""}`, textX, startY + 15);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        const heading = "Sales return invoice wise report";
        const headingWidth = doc.getTextWidth(heading);
        const pageWidth = doc.internal.pageSize.width;
        doc.text(heading, marginX + boxWidth - headingWidth - 10, startY + 8);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        const financialYearText = `Financial Year: ${tableData[0]?.financialYear || ""}`;
        const headingStartX = marginX + boxWidth - headingWidth - 10;
        doc.text(financialYearText, headingStartX, startY + 13);

        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        const fromDateLabel =
            "From : " + (LocalFormatDate(getDate(filters?.fromDate)) || "");
        const toDateLabel =
            "To : " + (LocalFormatDate(getDate(filters?.toDate)) || "");

        const tableStartY = startY + boxHeight + paddingBetweenHeaderAndTable;
        doc.text(fromDateLabel, marginX, tableStartY);
        doc.text(toDateLabel, marginX + 50, tableStartY);

        const tableBody = buildPdfTableBody(displayRows);

        autoTable(doc, {
            startY: 47,
            head: [
                [
                    "SI.No",
                    "Date",
                    "Day",
                    "Invoice Number",
                    "Name",
                    "Bill Value",
                    "Qty",
                    "Taxable",
                    "IGST",
                    "CGST",
                    "SGST",
                    "Total",
                    "Paid",
                    "Balance",
                    "Split Mode",
                ],
            ],
            body: tableBody,
            foot: [
                [
                    {
                        content: "Grand Total",
                        colSpan: 5,
                        styles: { fontStyle: "bold", halign: "right" },
                    },
                    {
                        content: rupeeFormat(grandTotal.billValue.toFixed(2)),
                        styles: { fontStyle: "bold" },
                    },
                    { content: "", styles: { fontStyle: "bold" } },
                    {
                        content: rupeeFormat(grandTotal.taxable.toFixed(2)),
                        styles: { fontStyle: "bold" },
                    },
                    {
                        content: rupeeFormat(grandTotal.igst.toFixed(2)),
                        styles: { fontStyle: "bold" },
                    },
                    {
                        content: rupeeFormat(grandTotal.cgst.toFixed(2)),
                        styles: { fontStyle: "bold" },
                    },
                    {
                        content: rupeeFormat(grandTotal.sgst.toFixed(2)),
                        styles: { fontStyle: "bold" },
                    },
                    {
                        content: rupeeFormat(grandTotal.lineTotal.toFixed(2)),
                        styles: { fontStyle: "bold" },
                    },
                    {
                        content: rupeeFormat(grandTotal.paid.toFixed(2)),
                        styles: { fontStyle: "bold" },
                    },
                    {
                        content: rupeeFormat(grandTotal.balance.toFixed(2)),
                        styles: { fontStyle: "bold" },
                    },
                    { content: "", styles: { fontStyle: "bold" } },
                ],
            ],
            theme: "grid",
            styles: { fontSize: 8, textColor: 0, lineColor: 0, lineWidth: 0.1 },
            headStyles: {
                fillColor: [255, 255, 255],
                textColor: 0,
                fontStyle: "bold",
                lineColor: 0,
                lineWidth: 0.1,
            },
            footStyles: {
                fillColor: [255, 255, 255],
                halign: "right",
            },
            margin: { top: 25, left: 10, right: 10 },
            columnStyles: {
                0: { halign: "center" },
                1: { halign: "center" },
                2: { halign: "center" },
                3: { halign: "center" },
                4: { halign: "left" },
                5: { halign: "center" },
                6: { halign: "right" },
                7: { halign: "right" },
                8: { halign: "right" },
                9: { halign: "right" },
                10: { halign: "right" },
                11: { halign: "right" },
                12: { halign: "right" },
                13: { halign: "right" },
                14: { halign: "center" },
            },
        });

        const pageCount = doc.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            const pageHeight = doc.internal.pageSize.height;
            const footerY = pageHeight - 15;
            doc.setFontSize(10);
            doc.text(`Page ${i} of ${pageCount}`, pageWidth - 30, footerY);
        }

        doc.save("sales_return_invoice_details.pdf");
    };

    const renderInvoiceCell = (
        row: InvoiceWiseDisplayRow,
        content: ReactNode,
    ) => {
        if (!row.isFirstLineOfInvoice) return null;
        return (
            <td rowSpan={row.invoiceRowSpan} className="align-middle">
                {content}
            </td>
        );
    };

    return (
        <>
            <SalesReturnReportFilter
                onSearch={handleSearch}
                from="Sales Return Invoice Wise"
            />
            <ReportGrandTotalBar show={hasSearched} amount={grandTotal.lineTotal} />
            {hasSearched && tableData.length > 0 ? (
                <Card className="mt-2 p-2 shadow-lg border-0 rounded-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="fw-bold">Sales return invoice details</h5>
                        <div>
                            <Button
                                className="me-2 btn-sm"
                                variant="success"
                                onClick={downloadCSV}
                            >
                                Download CSV
                            </Button>
                            <Button
                                className="me-2 btn-sm"
                                variant="success"
                                onClick={downloadPDF}
                            >
                                Download PDF
                            </Button>
                        </div>
                    </div>
                    <Table striped bordered hover className="mb-0">
                        <thead>
                            <tr>
                                <th>SI.No</th>
                                <th>Financial year</th>
                                <th>Date</th>
                                <th>Invoice number</th>
                                <th>Name</th>
                                <th>Bill value</th>
                                <th>Quantity</th>
                                <th>Taxable</th>
                                <th>IGST</th>
                                <th>CGST</th>
                                <th>SGST</th>
                                <th>Total</th>
                                <th>Paid</th>
                                <th>Balance</th>
                                <th>Split mode</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayRows.map((item, index) => (
                                <tr key={`${item.invoiceNo}-${index}`}>
                                    {renderInvoiceCell(item, item.siNo)}
                                    {renderInvoiceCell(item, item.financialYear)}
                                    {renderInvoiceCell(
                                        item,
                                        LocalFormatDate(item.date),
                                    )}
                                    {renderInvoiceCell(item, item.day)}
                                    {renderInvoiceCell(item, item.invoiceNo)}
                                    {renderInvoiceCell(item, item.customerName)}
                                    {renderInvoiceCell(
                                        item,
                                        rupeeFormat(item.billValue),
                                    )}
                                    <td>{item.quantity}</td>
                                    <td>{rupeeFormat(item.taxable)}</td>
                                    <td>{rupeeFormat(item.igst)}</td>
                                    <td>{rupeeFormat(item.cgst)}</td>
                                    <td>{rupeeFormat(item.sgst)}</td>
                                    <td>{rupeeFormat(item.total)}</td>
                                    {renderInvoiceCell(
                                        item,
                                        rupeeFormat(item.paid),
                                    )}
                                    {renderInvoiceCell(
                                        item,
                                        rupeeFormat(item.balance),
                                    )}
                                    {renderInvoiceCell(item, item.splitMode)}
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="fw-bold table-light">
                                <td colSpan={6} className="text-end">
                                    Grand Total
                                </td>
                                <td>{rupeeFormat(grandTotal.billValue)}</td>
                                <td></td>
                                <td>{rupeeFormat(grandTotal.taxable)}</td>
                                <td>{rupeeFormat(grandTotal.igst)}</td>
                                <td>{rupeeFormat(grandTotal.cgst)}</td>
                                <td>{rupeeFormat(grandTotal.sgst)}</td>
                                <td>{rupeeFormat(grandTotal.lineTotal)}</td>
                                <td>{rupeeFormat(grandTotal.paid)}</td>
                                <td>{rupeeFormat(grandTotal.balance)}</td>
                                <td />
                            </tr>
                        </tfoot>
                    </Table>
                </Card>
            ) : hasSearched ? (
                <h4 className="text-center fw-bold text-secondary">
                    No such data found!
                </h4>
            ) : null}
        </>
    );
};

export default SalesReturnInvoiceWise;
